// src/hooks/useEventTypeSchemas.js
// Hook for fetching and managing dynamic event type schemas from the backend.
// Follows the useJobSchemas pattern: fetch on mount, group by category, provide lookup.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useGraphQL } from '../graphql';

/**
 * Fallback event types used when the backend query is unavailable.
 * Mirrors the previously hardcoded EVENT_TYPES with inferred categories.
 */
const FALLBACK_EVENT_TYPES = [
    { eventType: 'tender.created', category: 'tender', description: 'A new tender was created', source: '', payload: [] },
    { eventType: 'tender.state_changed', category: 'tender', description: 'Tender processing state changed', source: '', payload: [{ name: 'tenderId', type: 'string', description: 'Tender ID' }, { name: 'fromState', type: 'string', description: 'Previous state' }, { name: 'toState', type: 'string', description: 'New state' }] },
    { eventType: 'tender.crawled', category: 'tender', description: 'Tender documents were crawled', source: '', payload: [] },
    { eventType: 'tender.enriched', category: 'tender', description: 'Tender was enriched via deep crawl', source: '', payload: [] },
    { eventType: 'tender.processed', category: 'tender', description: 'Tender documents processed', source: '', payload: [] },
    { eventType: 'tender.assessed', category: 'tender', description: 'Tender assessment completed', source: '', payload: [] },
    { eventType: 'tender.archived', category: 'tender', description: 'Tender was archived', source: '', payload: [] },
    { eventType: 'document.downloaded', category: 'document', description: 'Document was downloaded', source: '', payload: [] },
    { eventType: 'document.converted', category: 'document', description: 'Document was converted', source: '', payload: [] },
    { eventType: 'document.extracted', category: 'document', description: 'Document text was extracted', source: '', payload: [] },
    { eventType: 'job.completed', category: 'job', description: 'A job completed successfully', source: '', payload: [] },
    { eventType: 'job.failed', category: 'job', description: 'A job failed', source: '', payload: [] },
    { eventType: 'company.created', category: 'company', description: 'A new company was created', source: '', payload: [] },
    { eventType: 'company.fingerprint_created', category: 'company', description: 'Company fingerprint was created', source: '', payload: [] },
    { eventType: 'company.fingerprint_updated', category: 'company', description: 'Company fingerprint was updated', source: '', payload: [] },
    { eventType: 'user.created', category: 'user', description: 'A new user was created', source: '', payload: [] },
];

const CATEGORY_META = {
    tender: { label: 'Tender Events', order: 1 },
    document: { label: 'Document Events', order: 2 },
    job: { label: 'Job Events', order: 3 },
    company: { label: 'Company Events', order: 4 },
    user: { label: 'User Events', order: 5 },
};

export const useEventTypeSchemas = () => {
    const { executeQuery, queries } = useGraphQL();
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const fetchSchemas = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await executeQuery(queries.getEventTypeSchemas());
                if (cancelled) return;
                if (data?.eventTypeSchemas?.length) {
                    setSchemas(data.eventTypeSchemas);
                } else {
                    setSchemas(FALLBACK_EVENT_TYPES);
                }
            } catch (err) {
                if (cancelled) return;
                console.error('Failed to fetch event type schemas:', err);
                setError(err.message);
                setSchemas(FALLBACK_EVENT_TYPES);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchSchemas();
        return () => { cancelled = true; };
    }, []);

    const eventTypes = useMemo(() => {
        return schemas
            .map(s => ({
                ...s,
                payloadFieldNames: (s.payload || []).map(p => p.name),
            }))
            .sort((a, b) => {
                const orderA = CATEGORY_META[a.category]?.order ?? 99;
                const orderB = CATEGORY_META[b.category]?.order ?? 99;
                if (orderA !== orderB) return orderA - orderB;
                return a.eventType.localeCompare(b.eventType);
            });
    }, [schemas]);

    const eventTypesByCategory = useMemo(() => {
        const grouped = {};
        eventTypes.forEach(et => {
            const cat = et.category || 'other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(et);
        });
        return grouped;
    }, [eventTypes]);

    const categories = useMemo(() => {
        return Object.keys(eventTypesByCategory)
            .map(cat => ({
                id: cat,
                label: CATEGORY_META[cat]?.label || `${cat.charAt(0).toUpperCase() + cat.slice(1)} Events`,
                order: CATEGORY_META[cat]?.order ?? 99,
            }))
            .sort((a, b) => a.order - b.order);
    }, [eventTypesByCategory]);

    const getEventType = useCallback((eventType) => {
        return eventTypes.find(et => et.eventType === eventType) || null;
    }, [eventTypes]);

    return {
        eventTypes,
        eventTypesByCategory,
        categories,
        loading,
        error,
        getEventType,
    };
};
